import React, { useCallback, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import BrowseIcon from '@mui/icons-material/FolderOpen';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageSearchIcon from '@mui/icons-material/ImageSearch';
import LabelIcon from '@mui/icons-material/Label';
import ActionSettingsLayout from '../ActionSettingsHeader';
import { useActionSettings } from '../../hooks/useActionSettings';
import { useFolderPicker } from '../useFolderPicker';
import { useFolderRestrictions } from '../useFolderRestrictions';
import TagEditor from '../Widgets/TagEditor';

const generateRowId = () => `extract_image_row_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const createDefaultRow = (overrides = {}) => ({
  id: generateRowId(),
  outputTag: 'extracted_image',
  request: 'extract the most relevant image region',
  ...overrides,
});

const normalizeRow = (row, index = 0) => ({
  id: row?.id || `row_${index}`,
  outputTag: String(row?.outputTag || row?.tag || 'extracted_image').trim(),
  request: String(
    row?.request || row?.prompt || row?.question || row?.instruction || 'extract the most relevant image region',
  ).trim(),
  mode: String(row?.mode || row?.cropMode || row?.extractionMode || 'ai').trim().toLowerCase() === 'direct' ? 'direct' : 'ai',
});

const normalizeRows = (rows) => {
  const normalizedRows = Array.isArray(rows) ? rows.map((row, index) => normalizeRow(row, index)) : [];
  return normalizedRows.length > 0 ? normalizedRows : [normalizeRow(createDefaultRow(), 0)];
};

const stripRowIds = (rows) => normalizeRows(rows).map(({ id, ...row }) => row);

const ActionsExtractImageToTag = ({ caseid, tags, settingId, onSaveStatusChange }) => {
  const [sourceMode, setSourceMode] = useState('current');
  const [imagePath, setImagePath] = useState('');
  const [items, setItems] = useState([createDefaultRow()]);
  const [filePathError, setFilePathError] = useState('');
  const [itemsError, setItemsError] = useState('');
  const imagePathRef = useRef(null);

  const { restrictToUserFolder, allowedRoots, displayRoot } = useFolderRestrictions(caseid);
  const { handleBrowseDirectory, FolderBrowserComponent } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    allowFileSelection: true,
    onFolderSelected: (selectedPath) => {
      if (selectedPath) {
        setImagePath(selectedPath);
        setFilePathError('');
      }
    },
  });

  const {
    isEnabled,
    setIsEnabled,
    conditions,
    handleConditionsChange,
    isSaveDisabled,
    settingsLoaded,
    id,
    forceRunLoading,
    forceRunResult,
    popUpOpen,
    forceRunWarningOpen,
    forceRunWarningCount,
    handleSave,
    handleForceRun,
    handleForceRunConfirm,
    handleForceRunCancel,
    handleCloseStatusWindow,
  } = useActionSettings({
    settingId,
    caseid,
    actionType: 'extractimagetotag',
    onSaveStatusChange,
    customComparison: (initialData) => {
      if (!initialData) return false;
      const initialItems = stripRowIds(initialData.items || initialData.requests || []);
      const currentItems = stripRowIds(items);
      return (
        String(initialData.sourceMode || 'current') === sourceMode &&
        String(initialData.imagePath || '') === (sourceMode === 'other' ? imagePath.trim() : '') &&
        JSON.stringify(initialItems) === JSON.stringify(currentItems)
      );
    },
    customSaveData: () => {
      const normalizedItems = stripRowIds(items);
      return {
        sourceMode,
        imagePath: sourceMode === 'other' ? imagePath.trim() : '',
        items: normalizedItems,
        outputTag: normalizedItems[0]?.outputTag || 'extracted_image',
        request: normalizedItems[0]?.request || 'extract the most relevant image region',
        useConditions: true,
        conditions,
      };
    },
    customForceRunData: () => {
      const normalizedItems = stripRowIds(items);
      return {
        sourceMode,
        imagePath: sourceMode === 'other' ? imagePath.trim() : '',
        items: normalizedItems,
        outputTag: normalizedItems[0]?.outputTag || 'extracted_image',
        request: normalizedItems[0]?.request || 'extract the most relevant image region',
        useConditions: true,
        conditions,
      };
    },
    loadDataFunction: (data) => {
      let parsedData = {};
      if (data.data && typeof data.data === 'string') {
        try {
          parsedData = JSON.parse(data.data);
        } catch (error) {
          return;
        }
      } else if (data.customData) {
        parsedData = data.customData;
      }

      setSourceMode(parsedData.sourceMode || 'current');
      setImagePath(parsedData.imagePath || '');

      const loadedItems = parsedData.items || parsedData.requests;
      if (Array.isArray(loadedItems) && loadedItems.length > 0) {
        setItems(normalizeRows(loadedItems));
      } else {
        setItems([
          normalizeRow({
            outputTag: parsedData.outputTag || parsedData.tag || 'extracted_image',
            request: parsedData.request || parsedData.prompt || 'extract the most relevant image region',
            mode: parsedData.mode || parsedData.cropMode || parsedData.extractionMode,
          }),
        ]);
      }
    },
  });

  const handleBrowse = useCallback(() => {
    handleBrowseDirectory();
  }, [handleBrowseDirectory]);

  const handleSourceModeChange = useCallback((event) => {
    const nextMode = event.target.value;
    setSourceMode(nextMode);
    if (nextMode !== 'other') {
      setImagePath('');
      setFilePathError('');
    }
  }, []);

  const updateRow = useCallback((rowId, field, value) => {
    setItems((previousItems) =>
      previousItems.map((row) => (row.id === rowId ? { ...row, [field]: value } : row))
    );
  }, []);

  const addRow = useCallback(() => {
    setItems((previousItems) => [...previousItems, createDefaultRow({ outputTag: '', request: '' })]);
  }, []);

  const removeRow = useCallback((rowId) => {
    setItems((previousItems) => {
      if (previousItems.length <= 1) {
        return previousItems;
      }
      return previousItems.filter((row) => row.id !== rowId);
    });
  }, []);

  const handleCustomSave = useCallback(() => {
    let hasError = false;
    const normalizedItems = stripRowIds(items);

    if (sourceMode === 'other' && !imagePath.trim()) {
      setFilePathError('An image or PDF path is required.');
      hasError = true;
    } else if (filePathError) {
      setFilePathError('');
    }

    const invalidRowIndex = normalizedItems.findIndex(
      (row) => !String(row.outputTag || '').trim() || !String(row.request || '').trim()
    );
    if (invalidRowIndex >= 0) {
      const rowNumber = invalidRowIndex + 1;
      const row = normalizedItems[invalidRowIndex];
      const missingParts = [];
      if (!String(row.outputTag || '').trim()) missingParts.push('output tag');
      if (!String(row.request || '').trim()) missingParts.push('request');
      setItemsError(`Row ${rowNumber} requires a ${missingParts.join(' and ')}.`);
      hasError = true;
    } else if (new Set(normalizedItems.map((row) => row.outputTag)).size !== normalizedItems.length) {
      setItemsError('Each output tag must be unique.');
      hasError = true;
    } else if (itemsError) {
      setItemsError('');
    }

    if (!hasError) {
      handleSave();
    }
  }, [filePathError, handleSave, imagePath, items, itemsError, sourceMode]);

  return (
    <ActionSettingsLayout
      title="Extract Image To Tag"
      isEnabled={isEnabled}
      onEnabledChange={setIsEnabled}
      isSaveDisabled={isSaveDisabled}
      onSave={handleCustomSave}
      onForceRun={handleForceRun}
      forceRunLoading={forceRunLoading}
      forceRunResult={forceRunResult}
      popUpOpen={popUpOpen}
      onCloseStatusWindow={handleCloseStatusWindow}
      forceRunWarningOpen={forceRunWarningOpen}
      forceRunWarningCount={forceRunWarningCount}
      onForceRunConfirm={handleForceRunConfirm}
      onForceRunCancel={handleForceRunCancel}
      allowForceRunWithUnsavedChanges={settingsLoaded && !(typeof id === 'string' && id.startsWith('temp_'))}
      conditions={conditions}
      onConditionsChange={handleConditionsChange}
      tags={tags}
    >
      <Grid item xs={12}>
        <Card sx={{ mb: 2, borderRadius: '12px', overflow: 'hidden' }}>
          <CardHeader
            title={<Typography sx={{ fontWeight: 700 }}>Source</Typography>}
            sx={{ borderBottom: '1px solid #f0f0f8', backgroundColor: '#fafbff' }}
          />
          <CardContent>
            <FormControl component="fieldset" sx={{ mb: 2 }}>
              <RadioGroup row value={sourceMode} onChange={handleSourceModeChange}>
                <FormControlLabel value="current" control={<Radio size="small" />} label="Use current event file" />
                <FormControlLabel value="other" control={<Radio size="small" />} label="Use custom path" />
              </RadioGroup>
            </FormControl>

            {sourceMode === 'other' && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1 }}>
                  <TagEditor
                    caseid={caseid}
                    ref={imagePathRef}
                    value={imagePath}
                    onChange={setImagePath}
                    placeholder="Path to image or PDF"
                    singleLine
                    multiline={false}
                    onTagClick={() => {}}
                    tags={tags}
                  />
                  {filePathError && (
                    <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                      {filePathError}
                    </Typography>
                  )}
                </Box>
                <Button variant="outlined" onClick={handleBrowse} startIcon={<BrowseIcon />} sx={{ mt: 0.5 }}>
                  Browse
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card sx={{ mb: 2, borderRadius: '12px', overflow: 'hidden' }}>
          <CardHeader
            title={<Typography sx={{ fontWeight: 700 }}>Requests</Typography>}
            action={
              <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={addRow} sx={{ borderRadius: 999 }}>
                Add
              </Button>
            }
            sx={{ borderBottom: '1px solid #f0f0f8', backgroundColor: '#fafbff' }}
          />
          <CardContent>
            <Stack spacing={1.5}>
              {items.map((item, index) => (
                <Box
                  key={item.id}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    p: 2,
                    border: '1px solid #e8ebf4',
                    borderRadius: '12px',
                    backgroundColor: '#fff',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LabelIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Output Tag:
                      </Typography>
                      <TextField
                        size="small"
                        value={item.outputTag}
                        onChange={(event) => updateRow(item.id, 'outputTag', event.target.value)}
                        placeholder="extracted_image"
                        sx={{ width: 250 }}
                      />
                    </Box>
                    <IconButton
                      onClick={() => removeRow(item.id)}
                      disabled={items.length <= 1}
                      size="small"
                      sx={{ color: 'text.secondary' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ImageSearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        What to find:
                      </Typography>
                    </Box>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      size="small"
                      value={item.request}
                      onChange={(event) => updateRow(item.id, 'request', event.target.value)}
                      placeholder="Example: extract the logo in the top right corner"
                    />
                  </Box>
                </Box>
              ))}
            </Stack>
            {itemsError && (
              <Typography variant="body2" color="error" sx={{ mt: 1.5 }}>
                {itemsError}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>

      {FolderBrowserComponent}
    </ActionSettingsLayout>
  );
};

export default React.memo(ActionsExtractImageToTag);
